import React, { useState } from 'react';
import { Briefcase, BadgeDollarSign, TrendingUp, CalendarClock } from 'lucide-react';
import { PipelineData, PipelineRecord } from '../data/mockData';
import { PipelineDetailModal } from './PipelineDetailModal';
interface PipelineTilesProps {
  pipeline: PipelineData;
}
const formatCurrency = (val: number) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
  notation: val >= 1000000 ? 'compact' : 'standard'
}).format(val);
export function PipelineTiles({
  pipeline
}: PipelineTilesProps) {
  const [activeModal, setActiveModal] = useState<{
    title: string;
    subtitle: string;
    value: number;
    records: PipelineRecord[];
    accent: 'cyan' | 'emerald' | 'blue' | 'purple';
  } | null>(null);
  const tiles = [{
    id: 'active',
    label: 'Active Pipeline',
    value: pipeline.activePipeline,
    subtitle: 'Total loans in progress',
    icon: Briefcase,
    accent: 'cyan' as const,
    records: pipeline.details?.activeRecords || []
  }, {
    id: 'funded',
    label: 'Fundings MTD',
    value: pipeline.fundingsMTD,
    subtitle: 'Funded this month',
    icon: BadgeDollarSign,
    accent: 'emerald' as const,
    records: pipeline.details?.fundingRecords || []
  }, {
    id: 'proj-current',
    label: 'Projected Fundings',
    value: pipeline.projectedCurrentMonth,
    subtitle: 'Current month forecast',
    icon: TrendingUp,
    accent: 'blue' as const,
    records: pipeline.details?.projectedCurrentRecords || []
  }, {
    id: 'proj-next',
    label: 'Projected Fundings',
    value: pipeline.projectedNextMonth,
    subtitle: 'Next month forecast',
    icon: CalendarClock,
    accent: 'purple' as const,
    records: pipeline.details?.projectedNextRecords || []
  }];
  const accentStyles: Record<string, {
    icon: string;
    glow: string;
    border: string;
  }> = {
    cyan: {
      icon: 'text-cyan-400 bg-cyan-500/15',
      glow: 'bg-cyan-500',
      border: 'hover:border-cyan-500/50'
    },
    emerald: {
      icon: 'text-emerald-400 bg-emerald-500/15',
      glow: 'bg-emerald-500',
      border: 'hover:border-emerald-500/50'
    },
    blue: {
      icon: 'text-blue-400 bg-blue-500/15',
      glow: 'bg-blue-500',
      border: 'hover:border-blue-500/50'
    },
    purple: {
      icon: 'text-purple-400 bg-purple-500/15',
      glow: 'bg-purple-500',
      border: 'hover:border-purple-500/50'
    }
  };
  return <section data-id="element-360">
      <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2" data-id="element-361">
        <span className="w-1.5 h-5 bg-teal-500 rounded-full" data-id="element-362"></span>
        Pipeline & Fundings
      </h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-363">
        {tiles.map(tile => {
        const styles = accentStyles[tile.accent];
        const Icon = tile.icon;
        return <div key={tile.id} onClick={() => setActiveModal({
          title: tile.label,
          subtitle: tile.subtitle,
          value: tile.value,
          records: tile.records,
          accent: tile.accent
        })} className={`bg-slate-800/80 border border-slate-700/50 rounded-xl p-5 ${styles.border} transition-all duration-200 shadow-lg group relative cursor-pointer hover:bg-slate-800`} data-id="element-364">
              {/* Top glow */}
              <div className="absolute top-0 left-0 w-full overflow-hidden rounded-t-xl" data-id="element-365">
                <div className={`w-full h-0.5 opacity-50 group-hover:opacity-100 transition-opacity ${styles.glow}`} data-id="element-366" />
              </div>

              <div className="flex items-start justify-between mb-3" data-id="element-367">
                <div className={`p-2 rounded-lg ${styles.icon}`} data-id="element-368">
                  <Icon className="w-5 h-5" data-id="element-369" />
                </div>
              </div>

              <div className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-1" data-id="element-370">
                {formatCurrency(tile.value)}
              </div>

              <div className="text-sm font-medium text-slate-300" data-id="element-371">
                {tile.label}
              </div>
              <div className="text-sm text-slate-400 mt-0.5" data-id="element-372">
                {tile.subtitle}
              </div>
            </div>;
      })}
      </div>

      {activeModal && <PipelineDetailModal title={activeModal.title} subtitle={activeModal.subtitle} totalValue={activeModal.value} records={activeModal.records} accentColor={activeModal.accent} onClose={() => setActiveModal(null)} data-id="element-373" />}
    </section>;
}